import { Logger, LoggerService } from '@Shared/logger';

import { SETTINGS } from '@Config';
import { EntitlementRepository } from '../repositories/entitlement.repository';
import { Auth0Service, AuthenticationUtils } from '@Modules/auth_module';
import { OptionsModule } from '@Modules/options_module';
import { HttpService } from '@Shared/http/http.service';
import {
  CreateSessionRequestPayload,
  EntitlementRequest,
  EntitlementSnapshot,
  EntitlementSource,
  MNGSession,
  request,
} from '../_models';
import {
  EntitlerSessionResult,
  ServerResponse,
} from '@Shared/http/http.models';
import { SessionService } from '@Modules/session_module/services/session.service';

export class EntitlementService extends EntitlementRepository {
  private Logger: Logger = LoggerService.createLogger('EntitlementService');
  private static instance: EntitlementService;
  protected lastEntitlementSnapshot: EntitlementSnapshot | null = null;

  static get Instance() {
    if (this.instance === null || this.instance === undefined) {
      this.instance = new EntitlementService();
    }
    return this.instance;
  }

  constructor() {
    super();
    this.Logger.trace('****  CTOR ****');
  }

  setup() {
    this.Logger.trace('setup');
  }

  public async checkEntitlements(): Promise<EntitlementSnapshot> {
    this.Logger.trace('checkEntitlements');

    const webviewEntitlement = this.checkForWebviewJWT();

    const source: EntitlementSource = {
      source: 'webview_jwt',
      kind: 'webview_jwt',
    };

    if (webviewEntitlement?.queryParamDetected) {
      if (webviewEntitlement.isValid && webviewEntitlement.entitlement) {
        this.setLocalSession(webviewEntitlement.entitlement.session);
        return {
          status: 'valid',
          entitlement: webviewEntitlement.entitlement,
          source,
        };
      } else {
        return {
          status: 'error',
          error: Error(
            'The received JWT was invalid: ' +
              webviewEntitlement.validationError || 'Unknown reason',
          ),
          reason: 'invalid-webview-jwt',
          source,
        };
      }
    }

    const localEntitlementsResult = await this.checkLocal(this.localReaders);
    this.Logger.trace('localEntitlementsResult', localEntitlementsResult);

    if (localEntitlementsResult.anyValid) {
      //we have a valid local entitlement, no need to call the API.

      const { entitlement, source } = localEntitlementsResult.anyValid;
      const result: EntitlementSnapshot = {
        status: 'valid',
        entitlement: entitlement.jwt,
        source: { source: source, kind: 'local' },
      };

      this.setWindowMNGAuthentication(result);
      return result;
    }
    
    await SessionService.Instance.getSession();

    try {
      let serverReq: EntitlementRequest;
      if (localEntitlementsResult.firstExpired) {
        serverReq = request.refresh({
          jwt: localEntitlementsResult.firstExpired.entitlement.token,
          verified:
            (await Auth0Service.Instance.getUser())?.email_verified ?? false,
        });
      } else {
        if (localEntitlementsResult.firstUserMismatch) {
          // Stored entitlement belongs to a different Auth0 user.
          // Clear it instead of leaving wrong-user data in storage, then
          // fetch fresh entitlements for the currently authenticated Auth0 sub
          this.Logger.trace(
            'Local entitlement belongs to a different Auth0 user; clearing stale storage.',
            localEntitlementsResult.firstUserMismatch,
          );
          this.clearLocalEntitlements();
        }
        serverReq = request.create(await this.createInput());
      }
      this.Logger.trace(
        'Local entitlement is not valid, Calling API with payload.',
        serverReq,
      );

      let res = await this.server.exchange(serverReq);
      this.Logger.trace('server.exchange response', res);

      // Expired local/cookie JWTs from another env (or an old signing key) fail refresh with
      // INV_PAYLOAD. Fall back to create so login/entitlement can continue.
      if (!res.ok && serverReq.action === 'refresh') {
        this.Logger.warn(
          'Entitlement refresh failed; falling back to session/create',
        );
        serverReq = request.create(await this.createInput());
        res = await this.server.exchange(serverReq);
        this.Logger.trace('server.exchange create-fallback response', res);
      }

      if (res.ok) {
        //successful response from server, setting localSession
        this.setLocalSession(res.value.jwt.session);
        const result: EntitlementSnapshot = {
          status: 'valid',
          entitlement: res.value.jwt,
          source: { source: serverReq.action, kind: 'server' },
        };

        this.setWindowMNGAuthentication(result);
        this.callAllPostAuthEvents(result);
        return result;
      }

      const result: EntitlementSnapshot = {
        status: 'error',
        error: res,
        reason: 'server-failed',
        source: { source: serverReq.action, kind: 'server' },
      };

      this.setWindowMNGAuthentication(result);
      return result;
    } catch (error) {
      this.Logger.error('checkEntitlements server exchange failed', error);
      const result: EntitlementSnapshot = {
        status: 'error',
        error,
        reason: 'server-failed',
        source: {
          source: localEntitlementsResult.firstExpired ? 'refresh' : 'create',
          kind: 'server',
        },
      };
      this.setWindowMNGAuthentication(result);
      return result;
    }
  }

  protected async callAllPostAuthEvents(result: EntitlementSnapshot) {
    try {
      if (result.status == 'valid') {
        this.Logger.trace('Cycling through post auth callbacks...');
        const arrayOfPromises =
          window.MNGAuthentication?.postAuthEvents?.map(async (task) => {
            task(result.entitlement.session.entitlementSource);
          }) || [];
        await Promise.all(arrayOfPromises);
        this.Logger.trace('Finished cycling through post auth callbacks.');
      } else {
        this.Logger.trace(
          'Skipping post auth callbacks since entitlement is not valid',
        );
      }
    } catch (e) {
      this.Logger.trace('Failed to cycle through post auth callbacks: ', e);
    }
  }

  private setWindowMNGAuthentication(result: EntitlementSnapshot): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.lastEntitlementSnapshot = result;

    AuthenticationUtils.initializeMNGAuthenticationGlobal();
    // These are commented out for now, since they weren't available on the page early enough
    // window.MNGAuthentication!.isUserAdFree = () => {
    //   if (
    //     this.lastEntitlementSnapshot?.status === 'valid' &&
    //     this.lastEntitlementSnapshot?.entitlement?.session
    //   ) {
    //     this.Logger.trace(
    //       'Ad Free Check:',
    //       this.lastEntitlementSnapshot.entitlement.session.adFree,
    //     );
    //     return this.lastEntitlementSnapshot.entitlement.session.adFree === true;
    //   }
    //   return false;
    // };
    //
    // window.MNGAuthentication!.isUserSubscriber = () => {
    //   if (
    //     this.lastEntitlementSnapshot?.status === 'valid' &&
    //     this.lastEntitlementSnapshot?.entitlement?.session
    //   ) {
    //     this.Logger.trace(
    //       'Subscriber Check:',
    //       this.lastEntitlementSnapshot.entitlement.session.isEntitled,
    //     );
    //     return (
    //       this.lastEntitlementSnapshot.entitlement.session.isEntitled === true
    //     );
    //   }
    //   return false;
    // };
  }

  protected async createInput(): Promise<CreateSessionRequestPayload> {
    // Create the input for the entitlement request
    const user = await Auth0Service.Instance.getUser();
    if (!user?.sub) {
      throw new Error(
        'Cannot create entitlement session: no authenticated user',
      );
    }
    return {
      uuid: user.sub,
      publication: OptionsModule.Options.domain,
      platform: OptionsModule.Options.entitlement.platform,
    };
  }

  /**
   * Clears the local entitlement state this plugin owns: entitlement cookies, auth0_sub
   * and the stored __MNG_Session.
   *
   */
  public async clearEntitlementState(): Promise<void> {
    this.clearLocalEntitlements();
  }

  public getMNGSession(): MNGSession {
    return this.MNGSession;
  }

  /**
   * Clears the entitler's server-side cache for this user, and nothing else — pair it
   * with clearEntitlementState() when local and session-server state should go too.
   *
   * The entitler keys on user.sub, so this only works while the Auth0 user is still
   * hydrated. It must run before auth state is torn down, not after.
   */
  public async clearEntitlerCache(): Promise<Boolean> {
    const user = await Auth0Service.Instance.getUser();
    if (!user?.sub) {
      // No hydrated user (common on the first interaction right after page load).
      // Skip the server cache clear instead of throwing on user.sub.
      this.Logger.trace(
        'clearEntitlerCache: no authenticated user, skipping server cache clear',
      );
      return false;
    }
    const httpService = new HttpService(SETTINGS.entitler.baseUrl, 0);
    const response = await httpService.entitlerPost2<
      ServerResponse<EntitlerSessionResult>
    >(
      'session/create',
      { uuid: user.sub, publication: OptionsModule.Options.domain },
      SETTINGS.entitler.apiKey,
      'DELETE',
    );
    this.Logger.trace('clearEntitlerCache', response);
    return response.success === true;
  }
}
